import React, { useState, useRef, useEffect } from "react";
import { Label } from "reactstrap";
import closeIcon from "../../../../../assets/images/close-icon-blue.svg";
import arrowDown from "../../../../../assets/images/arrow-down-blue.svg";
import { UsersService } from "../../../../../globals/services/users.service";
import styles from "./index.module.scss";

const FacultyMultiSelect = ({ selectedFacultyUsers = [], handleFacultyForm }) => {
  const usersService = UsersService();

  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [showNoResults, setShowNoResults] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen && searchTerm !== "") {
      setSearchTerm("");
      fetchUsers(0, 20);
    }
  }, [isOpen])

  useEffect(() => {
    if (allUsers.length && showNoResults) {
      setShowNoResults(false)
    }
  }, [allUsers]);

  const fetchUsers = async (skip, limit, searchQuery = "") => {
    setIsLoading(true);

    try {
      const users = searchQuery
        ? await usersService.filterUserListByNameOrEmail(searchQuery)
        : await usersService.paginateUsersForUserModule(skip, limit);

      if (!users.length) {
        setHasMore(false);
        searchQuery && setShowNoResults(true);
      }
      else {
        !searchQuery && setHasMore(true);
      }

      const updatedUsers = users.map(({ _id, fullName }) => ({
        id: _id,
        name: fullName,
        checked: selectedFacultyUsers.some((f) => f.id === _id),
      }));

      setAllUsers((prevState) => {
        const newState = searchQuery
          ? updatedUsers
          : [...prevState, ...updatedUsers];
        return newState;
      });
    } catch (error) {
      console.error("Error fetching users: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchTerm(query);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setPagination({ skip: 0, limit: 20 });
      fetchUsers(0, 20, query);
    }, 300);
  };

  const handleSelectFaculty = (faculty) => {
    const { id } = faculty;
    const isAlreadySelected = selectedFacultyUsers.some((u) => u.id === id);
    const updatedTeamState = isAlreadySelected
    ? selectedFacultyUsers.map(user =>
        user.id === id ? { ...user, checked: !user.checked } : user
      )
    : [...selectedFacultyUsers, { ...faculty, checked: true }];

    handleFacultyForm(updatedTeamState);
        setIsOpen(false);
  };

  const handleOpenDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen && isFirstLoad) {
      fetchUsers(pagination.skip, pagination.limit);
      setIsFirstLoad(false);
    }
  };


  const handleScroll = () => {
    if (
      dropdownContentRef.current &&
      dropdownContentRef.current.scrollTop + dropdownContentRef.current.clientHeight >=
      dropdownContentRef.current.scrollHeight - 10 &&
      hasMore &&
      !isLoading
    ) {
      const newSkip = pagination.skip + pagination.limit;
      setPagination((prev) => ({ ...prev, skip: newSkip }));
      fetchUsers(newSkip, pagination.limit, searchTerm);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="col-12 col-md-6 px-2" ref={dropdownRef}>
      <h5 className="mt-4">
        <Label className={styles.label}>Faculty</Label>
      </h5>
      <div className={styles.selectContainer}>
        <div className={styles.selectInput} onClick={handleOpenDropdown}>
          <div className={styles.tagsContainer}>
            {selectedFacultyUsers.filter(faculty => faculty.checked).map((faculty) => (
              <div key={faculty.id} className={styles.tag}>
                <img
                  src={closeIcon}
                  alt="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectFaculty(faculty);
                  }}
                />
                {faculty.name}
              </div>
            ))}
          </div>
          <img
            src={arrowDown}
            alt="Expand dropdown"
            className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`}
          />
        </div>

        {isOpen && (
          <div
            className={styles.dropdown}
            ref={dropdownContentRef}
            onScroll={handleScroll}
          >
            <div className={styles.searchContainer}><input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
            />
            </div>

            {allUsers.map((faculty) => (
              <div
                key={faculty.id}
                className={styles.dropdownItem}
                onClick={() => handleSelectFaculty(faculty)}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedFacultyUsers.some((f) => f.id === faculty.id && f.checked)}
                  readOnly
                />
                <span className={styles.dropdownText}>{faculty.name}</span>
              </div>
            ))}
            {isLoading && (
              <div className={styles.loadingSpinner}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            {showNoResults && searchTerm && (
              <div className={styles.noResults}>No user found for "{searchTerm}"</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyMultiSelect;